import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import _orderBy from 'lodash/orderBy';
import { useLogout, useRepsWorksheetFiltersFromQuery, useUserContext } from '@blocs.auth';
import { SystemRoleCode } from '@minecraft.graphql-operations';
import appConfig from 'config/appConfig';
import { getTokenOrganizationId } from 'utils/Token';
import {
  accountDivisionVar,
  defaultGlobalPaginations,
  globalPaginationVars,
  rosterSortVars,
  rosterSortDefaults,
} from 'lib/cache';

const handleResetPaginationAndFilter = (): void => {
  globalPaginationVars(defaultGlobalPaginations);
  window.sessionStorage.setItem('ROSTER_SORT', JSON.stringify(rosterSortDefaults));
  rosterSortVars(rosterSortDefaults);
};

interface InitDivisionParams {
  userContext: ReturnType<typeof useUserContext>;
  search: string; // the search string from the router
  push: ReturnType<typeof useHistory>['push'];
  logout: () => void;
}

const initDivisionData = async ({ userContext, search, push, logout }: InitDivisionParams) => {
  const { myAccount, tokens, switchAuthContext } = userContext;

  if (!myAccount?.organizations?.length || !myAccount?.divisions?.length) {
    logout();

    return;
  }

  const currentTokenContextId = tokens?.accessParsed?.organizationId;
  const queryParams = new URLSearchParams(search);
  const queryDivisionId = Number(queryParams.get('division_id'));
  const localDivisionId = Number(localStorage.getItem('divisionId'));
  const defaultDivision = _orderBy(myAccount?.divisions, ['name'], ['asc'])?.[0];
  const hasQueryDivisionMatch = myAccount.divisions.find((div) => div.id === queryDivisionId);
  const hasLocalDivisionMatch = myAccount.divisions.find((div) => div.id === localDivisionId);

  let targetDivisionId = defaultDivision?.id;

  if (queryDivisionId && hasQueryDivisionMatch) {
    targetDivisionId = queryDivisionId;
  } else if (localDivisionId && hasLocalDivisionMatch) {
    targetDivisionId = localDivisionId;
  }

  // set up division vars
  accountDivisionVar({ selectedDivisionId: targetDivisionId });
  localStorage.setItem('divisionId', String(targetDivisionId));
  queryParams.delete('division_id');

  if (targetDivisionId !== currentTokenContextId) {
    // switch context if we should have a different division setup
    await switchAuthContext({ organizationId: targetDivisionId });
  }

  const shouldRedirect = (!queryDivisionId || !hasQueryDivisionMatch) && (!localDivisionId || !hasLocalDivisionMatch);

  if (shouldRedirect) {
    const isFromOnboarding = JSON.parse(queryParams.get('isFromOnboarding'));

    if (isFromOnboarding) {
      push(`/projects?${new URLSearchParams({ isFromOnboarding })}`);
    } else {
      queryParams.set('refresh', '1');
      push(`/projects?${queryParams}`);
      handleResetPaginationAndFilter();
    }
  }
};

const AuthStore = ({ children }): JSX.Element => {
  const { location, push } = useHistory();
  const userContext = useUserContext();
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { myAccount, tokens, loginError, updateError, isAuthenticating, isUpdating, switchAuthContext } = userContext;
  const [logout] = useLogout();
  const userOrgs = myAccount?.organizations;
  const queryParams = new URLSearchParams(location.search);

  // this handles predefined worksheet filters from email(if user already logged in)
  const handleRepsWorksheetFiltersFromQuery = useRepsWorksheetFiltersFromQuery();

  useEffect(() => {
    handleRepsWorksheetFiltersFromQuery(new URLSearchParams(location.search));
  }, [handleRepsWorksheetFiltersFromQuery, location.search]);

  const tokenDivisionId = getTokenOrganizationId(tokens.access);
  const handleDivisionChange = async (targetDivisionId: number, roleCode: SystemRoleCode): Promise<void> => {
    const res = await switchAuthContext({ organizationId: Number(targetDivisionId), role: roleCode });

    if (res.updateError) {
      // there is a catch wrapped around this where it invoked
      // that displays an error toast when this happens
      throw res.updateError;
    } else {
      accountDivisionVar({ selectedDivisionId: targetDivisionId });
      localStorage.setItem('divisionId', String(targetDivisionId));

      queryParams.set('refresh', '1');
      handleResetPaginationAndFilter();
      push(`/projects?${queryParams}`);
    }
  };

  const handleLogoutClick = (): void => {
    logout();
    handleResetPaginationAndFilter();
  };

  useEffect(() => {
    if (!isInitialized && !userContext.isUpdating) {
      initDivisionData({
        search: location.search,
        userContext,
        push,
        logout: () => logout(appConfig.noOrgsUlaUrl),
      }).catch((e) => {
        console.error('failed to initialize division', { e });
      });
      setIsInitialized(true);
    }
  }, [userContext, push, logout, isInitialized, location.search]);

  return children({
    handleDivisionChange,
    handleLogoutClick,
    selectedDivisionId: tokenDivisionId,
    shouldRenderPage: !updateError && !loginError && !isUpdating,
    shouldShowLoader: !isInitialized || isAuthenticating || userOrgs?.length === 0 || isUpdating,
  });
};

export default AuthStore;
